import {
  and,
  ArrayLayoutProps,
  composePaths,
  computeLabel,
  createDefaultValue,
  isObjectArray,
  isPlainLabel,
  uiTypeIs,
} from '@jsonforms/core'
import map from 'lodash/map'
import merge from 'lodash/merge'
import range from 'lodash/range'

import {
  ControlElement,
  findUISchema,
  getFirstPrimitiveProp,
  JsonFormsCellRendererRegistryEntry,
  JsonFormsRendererRegistryEntry,
  JsonFormsUISchemaRegistryEntry,
  JsonSchema,
  moveDown,
  moveUp,
  Resolve,
  update,
} from '@jsonforms/core'
import { areEqual, JsonFormsStateContext, ResolvedJsonFormsDispatch, withJsonFormsContext } from '@jsonforms/react'

import Toolbar from '@material-ui/core/Toolbar'

import AddIcon from '@material-ui/icons/Add'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import ArrowUpward from '@material-ui/icons/ArrowUpward'
import DeleteIcon from '@material-ui/icons/Delete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { ComponentType } from 'enzyme'
import get from 'lodash/get'
import React, { Dispatch, Fragment, ReducerAction, useMemo, useState } from 'react'
import uuid from 'uuid/v1'

import { useCallback } from 'react'

import { RankedTester, rankWith } from '@jsonforms/core'
import { withJsonFormsArrayLayoutProps } from '@jsonforms/react'

import Badge from '@material-ui/core/Badge'
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Accordion, AccordionDetails, AccordionSummary, Hidden, IconButton, styled, Tooltip, Grid, Typography } from 'opensolar-ui'
import { doNotTranslate } from 'util/misc'

const styles = createStyles(({ palette }: Theme) => ({
  badge: {
    color: palette.error.main,
  },
}))

export interface ValidationProps {
  errorMessages: string
  id: string
}

const _ValidationIcon: React.FC<ValidationProps & WithStyles<'badge'>> = ({ classes, errorMessages, id }) => {
  return (
    <Tooltip id={id} title={errorMessages}>
      <Badge className={classes.badge} badgeContent={errorMessages.split('\n').length}>
        <ErrorOutlineIcon color="inherit" />
      </Badge>
    </Tooltip>
  )
}
//@ts-ignore
let ValidationIcon = withStyles(styles, { name: 'ValidationIcon' })(_ValidationIcon)

export interface ArrayLayoutToolbarProps {
  label: string
  errors: string
}
let ArrayLayoutToolbar = React.memo(({ label, errors }: ArrayLayoutToolbarProps) => {
  return (
    <Toolbar disableGutters={true}>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Typography variant={'h6'}>{label}</Typography>
        </Grid>
        <Hidden smUp={errors.length === 0}>
          <Grid item>
            <ValidationIcon id="tooltip-validation" errorMessages={errors} />
          </Grid>
        </Hidden>
      </Grid>
    </Toolbar>
  )
})

const iconStyle: any = { float: 'right' }

interface OwnPropsOfExpandPanel {
  index: number
  path: string
  uischema: ControlElement
  schema: JsonSchema
  expanded: boolean
  renderers?: JsonFormsRendererRegistryEntry[]
  cells?: JsonFormsCellRendererRegistryEntry[]
  uischemas: JsonFormsUISchemaRegistryEntry[]
  rootSchema: JsonSchema
  enableMoveUp: boolean
  enableMoveDown: boolean
  config: any
  childLabelProp?: string
  handleExpansion(panel: string): (event: any, expanded: boolean) => void
}

interface StatePropsOfExpandPanel extends OwnPropsOfExpandPanel {
  childLabel: string
  childPath: string
  enableMoveUp: boolean
  enableMoveDown: boolean
}

/**
 * Dispatch props of a table control
 */
export interface DispatchPropsOfExpandPanel {
  addItemAt(path: string, index: number, item: any): (event: any) => void
  removeItems(path: string, toDelete: number[]): (event: any) => void
  moveUp(path: string, toMove: number): (event: any) => void
  moveDown(path: string, toMove: number): (event: any) => void
}

export interface ExpandPanelProps extends StatePropsOfExpandPanel, DispatchPropsOfExpandPanel {
  isEmptyList: boolean
  label: string
  total: number
  createDefault: () => any
}

const _ExpandPanelRenderer = (props: ExpandPanelProps) => {
  const [labelHtmlId] = useState(`id${uuid()}`)

  const {
    isEmptyList,
    childLabel,
    childPath,
    index,
    total,
    expanded,
    moveDown,
    moveUp,
    enableMoveDown,
    enableMoveUp,
    handleExpansion,
    removeItems,
    addItemAt,
    path,
    rootSchema,
    schema,
    uischema,
    uischemas,
    renderers,
    cells,
    config,
    label,
    createDefault,
  } = props

  const foundUISchema = useMemo(
    () => findUISchema(uischemas, schema, uischema.scope, path, undefined, uischema, rootSchema),
    [uischemas, schema, uischema.scope, path, uischema, rootSchema]
  )

  const appliedUiSchemaOptions = merge({}, config, uischema.options)
  const heading_uischema = appliedUiSchemaOptions['heading']

  // There might be a better way to do this using the appropriate context/wrapper.
  if (isEmptyList) {
    return <AddItemRow label={label} onClick={addItemAt(path, 0, createDefault())} alwaysShow={true} />
  }

  return (
    <>
      {index === 0 && <AddItemRow label={label} onClick={addItemAt(path, index, createDefault())} alwaysShow={false} />}
      <Accordion aria-labelledby={labelHtmlId} expanded={expanded} onChange={handleExpansion(childPath)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container alignItems={'center'}>
            <Grid item xs={7} md={9}>
              <Grid container alignItems={'center'}>
                <Grid item xs={10} md={11}>
                  {heading_uischema ? (
                    <ResolvedJsonFormsDispatch
                      schema={schema}
                      uischema={heading_uischema}
                      path={childPath}
                      key={childPath}
                      renderers={renderers}
                      cells={cells}
                    />
                  ) : (
                    <span id={labelHtmlId}>{childLabel}</span>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5} md={3}>
              <Grid container justify={'flex-end'}>
                <Grid item>
                  <Grid container direction="row" justify="center" alignItems="center">
                    {appliedUiSchemaOptions.showSortButtons ? (
                      <Fragment>
                        <Grid item>
                          <IconButton
                            onClick={moveUp(path, index)}
                            style={iconStyle}
                            disabled={!enableMoveUp}
                            aria-label={`Move up`}
                          >
                            <ArrowUpward />
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <IconButton
                            onClick={moveDown(path, index)}
                            style={iconStyle}
                            disabled={!enableMoveDown}
                            aria-label={`Move down`}
                          >
                            <ArrowDownward />
                          </IconButton>
                        </Grid>
                      </Fragment>
                    ) : (
                      ''
                    )}
                    <Grid item>
                      <IconButton onClick={removeItems(path, [index])} style={iconStyle} aria-label={`Delete`}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <ResolvedJsonFormsDispatch
            schema={schema}
            uischema={foundUISchema}
            path={childPath}
            key={childPath}
            renderers={renderers}
            cells={cells}
          />
        </AccordionDetails>
      </Accordion>
      <AddItemRow
        label={label}
        onClick={addItemAt(path, index + 1, createDefault())}
        alwaysShow={index === total - 1}
      />
    </>
  )
}

const AddItemRowContHidable = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  height: 12,
  transition: 'height 0.2s ease, opacity 0.2s ease',

  '&:hover': {
    opacity: 1,
    height: 36,
  },
})
const AddItemRowCont = styled('div')({
  width: '100%',
  textAlign: 'center',
})
let AddItemRow = ({ onClick, label, alwaysShow }) => {
  const Cont = alwaysShow ? AddItemRowCont : AddItemRowContHidable
  return (
    <Cont>
      <IconButton aria-label={`Add to ${label}`} size="small" onClick={onClick}>
        <AddIcon />
      </IconButton>
    </Cont>
  )
}

/**
 * Maps state to dispatch properties of an expand pandel control.
 *
 * @param dispatch the store's dispatch method
 * @returns {DispatchPropsOfArrayControl} dispatch props of an expand panel control
 */
export const ctxDispatchToExpandPanelProps: (dispatch: Dispatch<ReducerAction<any>>) => DispatchPropsOfExpandPanel = (
  dispatch
) => ({
  addItemAt: (path: string, index: number, item: any) => (event: any): void => {
    event.stopPropagation()
    dispatch(
      update(path, (array) => {
        if (!array) array = []
        array.splice(index, 0, item)
        return array
      })
    )
  },
  removeItems: (path: string, toDelete: number[]) => (event: any): void => {
    event.stopPropagation()
    dispatch(
      update(path, (array) => {
        toDelete
          .sort()
          .reverse()
          .forEach((s) => array.splice(s, 1))
        return array
      })
    )
  },
  moveUp: (path: string, toMove: number) => (event: any): void => {
    event.stopPropagation()
    dispatch(
      update(path, (array) => {
        moveUp(array, toMove)
        return array
      })
    )
  },
  moveDown: (path: string, toMove: number) => (event: any): void => {
    event.stopPropagation()
    dispatch(
      update(path, (array) => {
        moveDown(array, toMove)
        return array
      })
    )
  },
})

/**
 * Map state to control props.
 * @param state the JSON Forms state
 * @param ownProps any own props
 * @returns {StatePropsOfControl} state props for a control
 */
export const withContextToExpandPanelProps = (
  Component: ComponentType<ExpandPanelProps>
): ComponentType<OwnPropsOfExpandPanel> => ({ ctx, props }: JsonFormsStateContext & ExpandPanelProps) => {
  const dispatchProps = ctxDispatchToExpandPanelProps(ctx.dispatch)
  const { childLabelProp, schema, path, index, uischemas } = props
  const childPath = composePaths(path, `${index}`)
  const childData = Resolve.data(ctx.core.data, childPath)
  const childLabel = childLabelProp
    ? get(childData, childLabelProp, '')
    : get(childData, getFirstPrimitiveProp(schema), '')

  return <Component {...props} {...dispatchProps} childLabel={childLabel} childPath={childPath} uischemas={uischemas} />
}

export const withJsonFormsExpandPanelProps = (
  Component: ComponentType<ExpandPanelProps>
): ComponentType<OwnPropsOfExpandPanel> =>
  withJsonFormsContext(
    withContextToExpandPanelProps(
      React.memo(Component, (prevProps: ExpandPanelProps, nextProps: ExpandPanelProps) =>
        areEqual(prevProps, nextProps)
      )
    )
  )

let ExpandPanelRenderer = withJsonFormsExpandPanelProps(_ExpandPanelRenderer)

interface MaterialArrayLayoutState {
  expanded: string | boolean | null
}
class MaterialArrayLayout extends React.PureComponent<ArrayLayoutProps, MaterialArrayLayoutState> {
  state: MaterialArrayLayoutState = {
    expanded: null,
  }
  innerCreateDefaultValue = () => createDefaultValue(this.props.schema)
  handleChange = (panel: string) => (_event: any, expanded: boolean) => {
    this.setState({
      expanded: expanded ? panel : false,
    })
  }
  isExpanded = (index: number) => this.state.expanded === composePaths(this.props.path, `${index}`)
  render() {
    const {
      data,
      path,
      schema,
      uischema,
      errors,
      renderers,
      cells,
      label,
      required,
      rootSchema,
      config,
      uischemas,
    } = this.props
    const appliedUiSchemaOptions = merge({}, config, this.props.uischema.options)

    return (
      <div>
        <ArrayLayoutToolbar
          label={computeLabel(
            isPlainLabel(label) ? label : label.default,
            !!required,
            appliedUiSchemaOptions.hideRequiredAsterisk
          )}
          errors={errors}
        />
        <div>
          {data > 0 ? (
            map(range(data), (index) => {
              return (
                <ExpandPanelRenderer
                  index={index}
                  label={label}
                  expanded={this.isExpanded(index)}
                  schema={schema}
                  path={path}
                  handleExpansion={this.handleChange}
                  uischema={uischema}
                  renderers={renderers}
                  cells={cells}
                  key={index}
                  rootSchema={rootSchema}
                  enableMoveUp={index !== 0}
                  enableMoveDown={index < data - 1}
                  config={config}
                  childLabelProp={appliedUiSchemaOptions.elementLabelProp}
                  uischemas={uischemas}
                  createDefault={this.innerCreateDefaultValue}
                  total={data}
                />
              )
            })
          ) : (
            <>
              <p>{doNotTranslate('No data')}</p>
              <ExpandPanelRenderer
                isEmptyList={true}
                index={0}
                label={label}
                expanded={false}
                schema={schema}
                path={path}
                handleExpansion={this.handleChange}
                uischema={uischema}
                renderers={renderers}
                cells={cells}
                key={0}
                rootSchema={rootSchema}
                enableMoveUp={false}
                enableMoveDown={false}
                config={config}
                childLabelProp={appliedUiSchemaOptions.elementLabelProp}
                uischemas={uischemas}
                createDefault={this.innerCreateDefaultValue}
                total={data}
              />
            </>
          )}
        </div>
      </div>
    )
  }
}

export const MaterialArrayLayoutRenderer = ({
  visible,
  enabled,
  id,
  uischema,
  schema,
  label,
  rootSchema,
  renderers,
  cells,
  data,
  path,
  errors,
  uischemas,
  addItem,
}: ArrayLayoutProps) => {
  const addItemCb = useCallback((p: string, value: any) => addItem(p, value), [addItem])
  return (
    <Hidden xsUp={!visible}>
      <MaterialArrayLayout
        label={label}
        uischema={uischema}
        schema={schema}
        id={id}
        rootSchema={rootSchema}
        errors={errors}
        enabled={enabled}
        visible={visible}
        data={data}
        path={path}
        addItem={addItemCb}
        renderers={renderers}
        cells={cells}
        uischemas={uischemas}
      />
    </Hidden>
  )
}

let tester: RankedTester = rankWith(4, and(uiTypeIs('ExpandableTypedList'), isObjectArray))
let renderer = withJsonFormsArrayLayoutProps(MaterialArrayLayoutRenderer)

export default { renderer, tester }
